import React from "react";
import { useNavigate } from "react-router-dom";
import { Booking, Order } from "../../../Types/Dashboard";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { FaBook, FaShoppingCart, FaUser } from "react-icons/fa";

interface RecentActivityProps {
  recentBookings: Booking[];
  recentOrders: Order[];
  loading?: boolean;
}

export default function RecentActivity({
  recentBookings,
  recentOrders,
  loading,
}: RecentActivityProps) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className="bg-white rounded-lg shadow p-6 animate-pulse"
          >
            <div className="h-6 bg-gray-200 rounded w-1/2 mb-4"></div>
            <div className="space-y-3">
              {[...Array(3)].map((_, j) => (
                <div key={j} className="h-16 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), "dd MMM yyyy, HH:mm", { locale: fr });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "confirmed":
        return "bg-blue-100 text-blue-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
      {/* Recent Bookings */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FaBook className="text-green-500" />
            Dernières Sessions
          </h3>
          <button
            onClick={() => navigate("/app/bookings")}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Voir tout
          </button>
        </div>
        <div className="space-y-3">
          {!recentBookings || recentBookings.length === 0 ? (
            <p className="text-gray-500 text-sm">Aucune session récente</p>
          ) : (
            recentBookings.slice(0, 5).map((booking) => (
              <div
                key={booking.id}
                className="border-l-4 border-green-500 pl-3 py-2 hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/app/bookings/${booking.id}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {booking.student?.first_name} {booking.student?.last_name} - {booking.tutor?.first_name} {booking.tutor?.last_name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDate(booking.start_time)}
                    </p>
                  </div>
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(
                      booking.status
                    )}`}
                  >
                    {booking.status}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FaShoppingCart className="text-purple-500" />
            Dernières Commandes
          </h3>
          <button
            onClick={() => navigate("/app/orders")}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Voir tout
          </button>
        </div>
        <div className="space-y-3">
          {!recentOrders || recentOrders.length === 0 ? (
            <p className="text-gray-500 text-sm">Aucune commande récente</p>
          ) : (
            recentOrders.slice(0, 5).map((order) => (
              <div
                key={order.id}
                className="border-l-4 border-purple-500 pl-3 py-2 hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/app/orders/${order.id}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      Commande #{order.id}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDate(order.created_at)}
                    </p>
                  </div>
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(
                      order.status
                    )}`}
                  >
                    {order.status}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
          <FaUser className="text-blue-500" />
          Statistiques Rapides
        </h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded">
            <span className="text-sm text-gray-700">Sessions ce mois</span>
            <span className="text-lg font-bold text-blue-600">
              {recentBookings?.length || 0}
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-purple-50 rounded">
            <span className="text-sm text-gray-700">Commandes ce mois</span>
            <span className="text-lg font-bold text-purple-600">
              {recentOrders?.length || 0}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
